import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AppSummary } from '@appbuilder/shared';
import { appsApi, ApiClientError } from '../api/client';
import { Banner, Button, Card, Field } from '../components/ui';

export function AppsListPage(): JSX.Element {
  const [apps, setApps] = useState<AppSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AppSummary | null>(null);

  const load = async (): Promise<void> => {
    setError(null);
    try {
      setApps(await appsApi.list());
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Failed to load apps');
      setApps([]);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const count = apps?.length ?? 0;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Deployment console</h1>
          <p className="page-sub">
            Design &amp; edit in the App Builder Studio app — build, sign and download your
            store-ready packages here.
          </p>
        </div>
      </div>

      {error && <Banner>{error}</Banner>}

      {apps === null ? (
        <p className="muted">Loading…</p>
      ) : count === 0 ? (
        <EmptyState />
      ) : (
        <div className="app-grid">
          {apps.map((app) => (
            <AppCard key={app.id} app={app} onDelete={() => setDeleteTarget(app)} />
          ))}
        </div>
      )}

      {deleteTarget && (
        <DeleteAppModal
          app={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setDeleteTarget(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

// ─── App card ────────────────────────────────────────────────────────────────

function AppCard({ app, onDelete }: { app: AppSummary; onDelete: () => void }): JSX.Element {
  const published = app.currentVersionCode != null;
  const initial = app.name.charAt(0).toUpperCase() || 'A';
  return (
    <div className="app-card">
      <div className="app-card-head">
        <span className="app-card-icon" aria-hidden>
          {initial}
        </span>
        <div className="app-card-id">
          <Link to={`/apps/${app.id}/build`} className="app-card-title">
            {app.name}
          </Link>
          <span className="app-card-pkg mono">{app.androidPackage}</span>
        </div>
      </div>

      <div className="app-card-meta">
        <span className={`badge ${published ? 'badge-success' : 'badge-neutral'}`}>
          {published ? `Published · v${app.currentVersionCode}` : 'Draft'}
        </span>
      </div>

      <div className="app-card-actions">
        <Link to={`/apps/${app.id}/build`} className="btn btn-primary btn-sm">
          🚀 Build &amp; download
        </Link>
        <button type="button" className="btn btn-ghost btn-sm app-card-delete" onClick={onDelete}>
          Delete
        </button>
      </div>
    </div>
  );
}

// ─── Empty state (creation is app-only) ───────────────────────────────────────

function EmptyState(): JSX.Element {
  return (
    <Card>
      <div className="empty-state">
        <div className="empty-state-icon" aria-hidden>
          📱
        </div>
        <h2>Your apps are born on your phone</h2>
        <p>
          Create and design apps in the <strong>App Builder Studio</strong> mobile app. When
          you tap “Deploy to Production”, they appear here — ready to build, sign and download.
        </p>
      </div>
    </Card>
  );
}

// ─── Delete confirmation (type-to-confirm) ─────────────────────────────────────

function DeleteAppModal({
  app,
  onClose,
  onDeleted,
}: {
  app: AppSummary;
  onClose: () => void;
  onDeleted: () => void;
}): JSX.Element {
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const matches = confirmText.trim() === app.name;

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const submit = async (): Promise<void> => {
    if (!matches || busy) return;
    setBusy(true);
    setError(null);
    try {
      await appsApi.remove(app.id);
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Failed to delete app');
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !busy && onClose()}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <h2 id="delete-modal-title">Delete “{app.name}”?</h2>
        </div>
        <div className="modal-body">
          {error && <Banner>{error}</Banner>}
          <p>
            This <strong>permanently</strong> deletes the app and everything attached to it:
          </p>
          <ul className="modal-list">
            <li>All builds and downloadable APK / AAB artifacts</li>
            <li>Every saved configuration version</li>
            <li>
              The <strong>signing keystore</strong> — if this app is already on Google Play, you
              could no longer ship updates to it
            </li>
            <li>Uploaded icon, splash &amp; images</li>
          </ul>
          <p className="danger-text">This action cannot be undone.</p>
          <Field
            label="Type the app name to confirm"
            hint={`Exactly: ${app.name}`}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={app.name}
            autoFocus
          />
        </div>
        <div className="modal-actions">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => void submit()} disabled={!matches} loading={busy}>
            Delete forever
          </Button>
        </div>
      </div>
    </div>
  );
}
